import React, { useMemo, useRef, useState } from 'react'
import { Minus, Plus, ScanBarcode, Trash2 } from 'lucide-react'
import Swal from 'sweetalert2'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { posService } from '@/services/posService'
import type { PosBank, PosBonusScheme, PosGetItemByBarcodeResponse } from '@/api/types'
import type { PosSavePayload } from '@/services/posService'

type Money = number

type CatalogItem = {
  id: string
  code: string
  name: string
  price: Money
  taxRate: number // e.g. 0.15
  maxQty: number
  discountPercent?: number
  bonusScheme?: PosBonusScheme | null
  discountScheme?: unknown | null
  meta?: PosGetItemByBarcodeResponse
}

type CartLine = {
  itemId: string
  qty: number
  discount: Money // per line discount (absolute)
  bonusQty: number
}

function money(n: number) {
  const safe = Number.isFinite(n) ? n : 0
  return safe.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function clampInt(n: number, min: number, max: number) {
  const x = Math.trunc(n)
  if (x < min) return min
  if (x > max) return max
  return x
}

function safeNumber(n: unknown, fallback = 0): number {
  const x = typeof n === 'number' ? n : Number(n)
  return Number.isFinite(x) ? x : fallback
}

function toPercentRate(pct: unknown): number {
  const n = safeNumber(pct, 0)
  if (n <= 0) return 0
  return n / 100
}

function inferDiscountPercent(discountScheme: unknown | null | undefined): number | undefined {
  if (!discountScheme || typeof discountScheme !== 'object') return undefined
  const d = discountScheme as Record<string, unknown>
  const raw =
    d.discountPercent ??
    d.DiscountPercent ??
    d.percent ??
    d.Percent ??
    d.discountPct ??
    d.DiscountPct ??
    undefined
  const pct = safeNumber(raw, NaN)
  return Number.isFinite(pct) && pct > 0 ? pct : undefined
}

function parseBonusScheme(raw: unknown): PosBonusScheme | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  const bonusItemId = Number(d.bonusItemId ?? d.BonusItemId ?? 0)
  const purchaseQuantity = Number(d.purchaseQuantity ?? d.PurchaseQuantity ?? 0)
  const bonusQuantity = Number(d.bonusQuantity ?? d.BonusQuantity ?? 0)
  if (!Number.isFinite(bonusItemId) || bonusItemId <= 0) return null
  if (!Number.isFinite(purchaseQuantity) || purchaseQuantity <= 0) return null
  if (!Number.isFinite(bonusQuantity) || bonusQuantity <= 0) return null
  return {
    bonusLineId: Number(d.bonusLineId ?? d.BonusLineId ?? 0),
    bonusSchemeId: Number(d.bonusSchemeId ?? d.BonusSchemeId ?? 0),
    schemeCode: String(d.schemeCode ?? d.SchemeCode ?? ''),
    promotionItemId: Number(d.promotionItemId ?? d.PromotionItemId ?? 0),
    purchaseQuantity,
    bonusItemId,
    bonusItemName: String(d.bonusItemName ?? d.BonusItemName ?? ''),
    bonusBatchId: Number(d.bonusBatchId ?? d.BonusBatchId ?? 0),
    bonusQuantity,
    eligibleBonusQuantity: Number(d.eligibleBonusQuantity ?? d.EligibleBonusQuantity ?? 0),
    maxBonusQuantity: Number(d.maxBonusQuantity ?? d.MaxBonusQuantity ?? 0),
  }
}

function bonusQtyFor(soldQty: number, scheme: PosBonusScheme | null | undefined): number {
  if (!scheme) return 0
  const buy = Math.trunc(safeNumber(scheme.purchaseQuantity, 0))
  const give = Math.trunc(safeNumber(scheme.bonusQuantity, 0))
  if (buy <= 0 || give <= 0) return 0
  const eligible = Math.floor(Math.max(0, soldQty) / buy) * give
  const cap = Math.max(0, Math.trunc(safeNumber(scheme.maxBonusQuantity, 0)))
  return Math.min(eligible, cap)
}

function mapPosResponseToCatalogItem(x: PosGetItemByBarcodeResponse): CatalogItem {
  const code = String(x.matchedBarcode ?? '').trim()
  const name = String(x.itemNameEn || x.itemNameAr || '').trim() || `Item ${x.itemId}`
  const taxRate = toPercentRate(x.salesTax?.taxPercent ?? 0)
  const maxQty = Math.max(0, Math.trunc(safeNumber(x.maxQuantity, 0)))
  return {
    id: String(x.itemId),
    code,
    name,
    price: safeNumber(x.unitSellingPrice, 0),
    taxRate,
    maxQty,
    discountPercent: inferDiscountPercent(x.discountScheme),
    bonusScheme: parseBonusScheme(x.bonusScheme),
    discountScheme: x.discountScheme ?? null,
    meta: x,
  }
}

function KeypadButton({
  children,
  onClick,
  className,
  variant = 'outline',
}: {
  children: React.ReactNode
  onClick: () => void
  className?: string
  variant?: React.ComponentProps<typeof Button>['variant']
}) {
  return (
    <Button
      type="button"
      variant={variant}
      onClick={onClick}
      className={[
        'h-12 w-full rounded-md text-base font-medium',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </Button>
  )
}

export const PointOfSales: React.FC = () => {
  const [scan, setScan] = useState('')
  const scanRef = useRef<HTMLInputElement | null>(null)
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)
  const [scanError, setScanError] = useState<string>('')

  const [cart, setCart] = useState<CartLine[]>([])
  const [catalog, setCatalog] = useState<Record<string, CatalogItem>>({})
  const [paid, setPaid] = useState<string>('0')
  const [payOpen, setPayOpen] = useState(false)
  const [banks, setBanks] = useState<PosBank[]>([])
  const [banksLoading, setBanksLoading] = useState(false)
  const [bankId, setBankId] = useState<string>('')
  const [payError, setPayError] = useState('')
  const [saving, setSaving] = useState(false)

  const catalogByCode = useMemo(() => {
    const map = new Map<string, CatalogItem>()
    for (const it of Object.values(catalog)) map.set(it.code, it)
    return map
  }, [catalog])

  const catalogById = useMemo(() => {
    const map = new Map<string, CatalogItem>()
    for (const it of Object.values(catalog)) map.set(it.id, it)
    return map
  }, [catalog])

  const rows = useMemo(() => {
    return cart
      .map((line) => {
        const item = catalogById.get(line.itemId)
        if (!item) return null
        const lineSubTotal = item.price * line.qty
        const percentDiscount =
          item.discountPercent != null ? (lineSubTotal * item.discountPercent) / 100 : 0
        const effectiveDiscount = Math.max(0, line.discount + percentDiscount)
        const lineTax = (lineSubTotal - effectiveDiscount) * item.taxRate
        const lineTotal = lineSubTotal - effectiveDiscount + lineTax
        return { item, line, lineSubTotal, lineTax, lineTotal, effectiveDiscount }
      })
      .filter(Boolean) as Array<{
      item: CatalogItem
      line: CartLine
      lineSubTotal: Money
      lineTax: Money
      lineTotal: Money
      effectiveDiscount: Money
    }>
  }, [cart, catalogById])

  const totals = useMemo(() => {
    const subTotal = rows.reduce((a, r) => a + r.lineSubTotal, 0)
    const discount = rows.reduce((a, r) => a + r.effectiveDiscount, 0)
    const tax = rows.reduce((a, r) => a + r.lineTax, 0)
    const total = rows.reduce((a, r) => a + r.lineTotal, 0)
    return { subTotal, discount, tax, total }
  }, [rows])

  const paidAmount = useMemo(() => {
    const n = Number(paid)
    return Number.isFinite(n) ? n : 0
  }, [paid])

  const change = useMemo(() => Math.max(0, paidAmount - totals.total), [paidAmount, totals.total])

  const addByCode = async (codeRaw: string) => {
    const code = codeRaw.trim()
    if (!code) return
    if (!branchId) {
      setScanError('Select branch first.')
      return
    }

    setScanError('')

    let item = catalogByCode.get(code)
    if (!item) {
      try {
        const res = await posService.getItemByBarcode({ branchId, barcode: code })
        if (!res) {
          setScanError('Item not found.')
          return
        }
        const mapped = mapPosResponseToCatalogItem(res)
        item = mapped
        setCatalog((prev) => ({ ...prev, [mapped.id]: mapped }))
      } catch (e) {
        setScanError('Failed to load item.')
        return
      }
    }

    setCart((prev) => {
      const idx = prev.findIndex((l) => l.itemId === item.id)
      if (idx === -1) {
        const qty = 1
        return [...prev, { itemId: item.id, qty, discount: 0, bonusQty: bonusQtyFor(qty, item.bonusScheme) }]
      }
      const next = [...prev]
      const max = item.maxQty > 0 ? item.maxQty : 9999
      const qty = clampInt(next[idx].qty + 1, 1, max)
      next[idx] = { ...next[idx], qty, bonusQty: bonusQtyFor(qty, item.bonusScheme) }
      return next
    })
  }

  const onScanSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const val = scan
    setScan('')
    await addByCode(val)
    scanRef.current?.focus()
  }

  const incQty = (itemId: string) => {
    const item = catalogById.get(itemId)
    const max = item?.maxQty ?? 9999
    const cap = max > 0 ? max : 9999
    setCart((prev) =>
      prev.map((l) => {
        if (l.itemId !== itemId) return l
        const qty = clampInt(l.qty + 1, 1, cap)
        return { ...l, qty, bonusQty: bonusQtyFor(qty, item?.bonusScheme) }
      }),
    )
  }

  const decQty = (itemId: string) => {
    const item = catalogById.get(itemId)
    setCart((prev) =>
      prev
        .map((l) => {
          if (l.itemId !== itemId) return l
          const qty = clampInt(l.qty - 1, 1, 9999)
          return { ...l, qty, bonusQty: bonusQtyFor(qty, item?.bonusScheme) }
        })
        .filter((l) => l.qty > 0),
    )
  }

  const removeLine = (itemId: string) => {
    setCart((prev) => prev.filter((l) => l.itemId !== itemId))
  }

  const clearCart = () => {
    setCart([])
    setPaid('0')
    setScan('')
    setScanError('')
    scanRef.current?.focus()
  }

  const keypad = {
    appendDigit(d: string) {
      setPaid((prev) => {
        const cur = prev.trim()
        if (cur === '0') return d
        return `${cur}${d}`.slice(0, 12)
      })
    },
    appendDot() {
      setPaid((prev) => {
        const cur = prev.trim()
        if (!cur) return '0.'
        if (cur.includes('.')) return cur
        return `${cur}.`
      })
    },
    del() {
      setPaid((prev) => {
        const cur = prev.trim()
        if (cur.length <= 1) return '0'
        const next = cur.slice(0, -1)
        return next || '0'
      })
    },
    enter() {
      scanRef.current?.focus()
    },
    focusScan() {
      scanRef.current?.focus()
    },
  }

  const pay = () => {
    if (rows.length === 0) return
    if (!branchId) {
      setPayError('Select branch first.')
      return
    }
    setPayError('')
    setPayOpen(true)
    setBanksLoading(true)
    void (async () => {
      try {
        const list = await posService.getBanks(branchId)
        setBanks(list)
      } catch {
        setBanks([])
      } finally {
        setBanksLoading(false)
      }
    })()
  }

  const onConfirmPay = async () => {
    if (!branchId) {
      setPayError('Select branch first.')
      return
    }
    const bId = Number(bankId)
    if (!Number.isFinite(bId) || bId <= 0) {
      setPayError('Select bank.')
      return
    }
    if (rows.length === 0) return

    setPayError('')
    setSaving(true)
    try {
      const payload: PosSavePayload = {
        branchId,
        subTotal: totals.subTotal,
        totalTax: totals.tax,
        totalDiscount: totals.discount,
        total: totals.total,
        bankId: bId,
        invoiceLines: rows.map((r) => {
          const meta = r.item.meta
          const itemId = Number(r.item.id)
          const batchId = Number(meta?.activeBatchId ?? 0)
          const taxId = Number(meta?.salesTaxId ?? meta?.purchaseTaxId ?? 0)
          const taxPercent = safeNumber(meta?.salesTax?.taxPercent ?? 0, 0)
          const unitCostPrice = safeNumber(meta?.unitCostPrice ?? 0, 0)
          const unitSellingPrice = safeNumber(meta?.unitSellingPrice ?? r.item.price, r.item.price)
          const discountPercent = safeNumber(r.item.discountPercent ?? 0, 0)
          const itemPrice = r.lineSubTotal
          const discount = r.effectiveDiscount
          const taxableAmount = Math.max(0, itemPrice - discount)
          const lineAmount = r.lineTotal
          const bonus = r.item.bonusScheme
          const bonusQty = r.line.bonusQty ?? 0

          return {
            itemId: Number.isFinite(itemId) ? itemId : 0,
            batchId: Number.isFinite(batchId) ? batchId : 0,
            quantity: r.line.qty,
            unitCostPrice,
            unitSellingPrice,
            discount,
            discountPercent,
            itemPrice,
            taxableAmount,
            taxId: Number.isFinite(taxId) ? taxId : 0,
            totalTax: r.lineTax,
            taxPercent,
            bonusId: bonusQty > 0 ? Number(bonus?.bonusSchemeId ?? 0) : 0,
            bonusItemId: bonusQty > 0 ? Number(bonus?.bonusItemId ?? 0) : 0,
            bonusBatchId: bonusQty > 0 ? Number(bonus?.bonusBatchId ?? 0) : 0,
            bonusQuantity: bonusQty,
            lineAmount,
          }
        }),
      }

      console.log('POS Save payload', payload)
      await posService.save(payload)

      // Close POS modal / reset UI before showing success
      setPayOpen(false)
      clearCart()

      await Swal.fire({
        icon: 'success',
        title: 'Purchase Success',
        text: 'POS sale saved successfully.',
        timer: 1200,
        showConfirmButton: false,
        allowOutsideClick: false,
      })
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'Save failed',
        text: 'Could not save POS invoice.',
        confirmButtonText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="h-full bg-[#f3f6fb] px-6 py-5">
      <div className="grid h-full grid-cols-1 gap-6 xl:grid-cols-[1fr_420px]">
        {/* LEFT */}
        <div className="flex h-full flex-col">
          {/* Scan input */}
          <form onSubmit={onScanSubmit} className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                ref={scanRef}
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                placeholder="ENTER ITEM CODE OR SCAN PRODUCT"
                className="h-12 bg-white uppercase tracking-wide"
                inputMode="numeric"
                autoComplete="off"
              />
              {scanError ? (
                <div className="mt-1 text-xs text-red-600">{scanError}</div>
              ) : null}
            </div>
            <Button type="submit" className="h-12 px-8">
              Enter
            </Button>
          </form>

          {/* Cart table area (fills remaining height) */}
          <div className="mt-4 flex-1 overflow-hidden rounded-sm border bg-white">
            <div className="h-full overflow-auto">
              <Table className="min-w-[820px]">
                <TableHeader className="bg-[#0a2236]">
                  <TableRow className="border-b border-[#13354f] hover:bg-[#0a2236]">
                    <TableHead className="h-10 text-[11px] font-semibold tracking-widest text-white">
                      PRODUCT
                    </TableHead>
                    <TableHead className="h-10 text-[11px] font-semibold tracking-widest text-white">
                      PRICE
                    </TableHead>
                    <TableHead className="h-10 text-[11px] font-semibold tracking-widest text-white">
                      QTY
                    </TableHead>
                    <TableHead className="h-10 text-[11px] font-semibold tracking-widest text-white">
                      DISCOUNT
                    </TableHead>
                    <TableHead className="h-10 text-[11px] font-semibold tracking-widest text-white">
                      TAX
                    </TableHead>
                    <TableHead className="h-10 text-[11px] font-semibold tracking-widest text-white">
                      BONUS_QTY
                    </TableHead>
                    <TableHead className="h-10 text-[11px] font-semibold tracking-widest text-white text-right">
                      TOTAL
                    </TableHead>
                    <TableHead className="h-10 text-[11px] font-semibold tracking-widest text-white w-[52px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="h-[360px] p-0 align-top">
                        <div className="h-full w-full" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(({ item, line, lineTax, lineTotal, effectiveDiscount }) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          <div className="leading-tight">
                            <div className="truncate">{item.name}</div>
                            <div className="text-xs text-muted-foreground">Code: {item.code}</div>
                          </div>
                        </TableCell>
                        <TableCell className="tabular-nums">{money(item.price)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => decQty(item.id)}
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <div className="min-w-[28px] text-center tabular-nums">{line.qty}</div>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => incQty(item.id)}
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {item.discountPercent != null
                            ? `${money(item.discountPercent)}% (${money(effectiveDiscount)})`
                            : money(effectiveDiscount)}
                        </TableCell>
                        <TableCell className="tabular-nums">{money(lineTax)}</TableCell>
                        <TableCell className="tabular-nums">{line.bonusQty}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(lineTotal)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => removeLine(item.id)}
                            aria-label="Remove line"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Bottom totals strip */}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
            <div className="rounded-sm border bg-white p-4">
              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="tracking-widest text-muted-foreground">SUB_TOTAL :</span>
                  <span className="tabular-nums">{money(totals.subTotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="tracking-widest text-muted-foreground">DISCOUNT :</span>
                  <span className="tabular-nums">{money(totals.discount)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="tracking-widest text-muted-foreground">TAX :</span>
                  <span className="tabular-nums">{money(totals.tax)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="tracking-widest text-muted-foreground">TOTAL_ITEMS :</span>
                  <span className="tabular-nums">{cart.reduce((a, l) => a + l.qty, 0)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-sm border bg-white p-4">
              <div className="text-center">
                <div className="text-xs tracking-widest text-muted-foreground">TOTAL_AMOUNT</div>
                <div className="mt-3 text-2xl font-semibold tabular-nums">{money(totals.total)}</div>
              </div>
            </div>
          </div>

          {/* PAY bar */}
          <div className="mt-4">
            <Button
              type="button"
              onClick={pay}
              disabled={rows.length === 0}
              className="h-12 w-full rounded-none bg-[#0a2236] text-white hover:bg-[#0a2236]/90 tracking-widest"
            >
              PAY
            </Button>
            {payError ? <div className="mt-2 text-xs text-red-600">{payError}</div> : null}
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex h-full flex-col rounded-sm border bg-white">
          <div className="flex-1 p-6 text-center">
            <div className="mt-10 text-sm text-muted-foreground">Demo</div>
          </div>

          <div className="border-t p-4">
            <div className="grid grid-cols-3 gap-3">
              {['7', '8', '9', '4', '5', '6', '1', '2', '3'].map((d) => (
                <KeypadButton key={d} onClick={() => keypad.appendDigit(d)} className="h-11">
                  {d}
                </KeypadButton>
              ))}
              <KeypadButton onClick={keypad.appendDot} className="h-11">
                .
              </KeypadButton>
              <KeypadButton onClick={() => keypad.appendDigit('0')} className="h-11">
                0
              </KeypadButton>
              <KeypadButton onClick={keypad.del} className="h-11 font-semibold">
                Del
              </KeypadButton>
              <KeypadButton
                onClick={keypad.enter}
                className="col-span-3 h-11 bg-white"
                variant="outline"
              >
                Enter
              </KeypadButton>
            </div>

            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <span>Paid</span>
              <span className="tabular-nums">{money(paidAmount)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>Change</span>
              <span className="tabular-nums">{money(change)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* PAY MODAL */}
      <Dialog
        open={payOpen}
        onOpenChange={(open) => {
          setPayOpen(open)
          if (!open) {
            setBankId('')
            setPayError('')
          }
        }}
      >
        <DialogContent
          className={[
            'max-w-4xl p-0 gap-0 overflow-hidden border shadow-2xl rounded-md',
            'border-[#13354f]',
            // style the built-in close button (DialogPrimitive.Close)
            '[&>button]:opacity-90 [&>button]:text-white [&>button]:hover:opacity-100 [&>button]:focus:ring-0',
            '[&>button]:right-5 [&>button]:top-4 [&>button]:bg-transparent [&>button]:data-[state=open]:bg-transparent',
          ].join(' ')}
        >
          <DialogHeader className="bg-[#0a2236] px-6 py-4">
            <DialogTitle className="text-white text-lg font-semibold tracking-widest">PAY</DialogTitle>
          </DialogHeader>

          <div className="bg-white">
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]">
              {/* left labels */}
              <div className="bg-[#0a2236] text-white">
                {[
                  'TOTAL_PAYABLE:',
                  'BANK:',
                  'PAID:',
                  'CHANGE:',
                  'DUE:',
                  'CUSTOMER:',
                ].map((label) => (
                  <div
                    key={label}
                    className="h-16 border-b border-[#13354f] flex items-center px-8 text-sm tracking-widest"
                  >
                    {label}
                  </div>
                ))}
              </div>

              {/* right inputs */}
              <div className="bg-white">
                <div className="h-16 border-b border-gray-200 flex items-center px-8">
                  <div className="text-4xl font-semibold tabular-nums text-gray-800">
                    {money(totals.total)}
                  </div>
                </div>

                <div className="h-16 border-b border-gray-200 flex items-center px-8">
                  <div className="w-full">
                    <Select value={bankId} onValueChange={setBankId}>
                      <SelectTrigger className="h-12 bg-gray-100 border-gray-200 shadow-sm focus:ring-2 focus:ring-[#0a2236]/30">
                        <SelectValue
                          placeholder={banksLoading ? 'Loading…' : 'Select bank'}
                        />
                      </SelectTrigger>
                      <SelectContent searchable>
                        {banks.map((b) => {
                          const label =
                            (b.bankNameEn || b.bankNameAr || '').trim() ||
                            `Bank ${b.bankId}`
                          return (
                            <SelectItem key={b.bankId} value={String(b.bankId)}>
                              {label}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="h-16 border-b border-gray-200 flex items-center px-8">
                  <Input
                    value={paid}
                    onChange={(e) => setPaid(e.target.value)}
                    className="h-12 bg-gray-100 border-gray-200 shadow-sm tabular-nums"
                    inputMode="decimal"
                    autoComplete="off"
                  />
                </div>

                <div className="h-16 border-b border-gray-200 flex items-center px-8">
                  <div className="text-lg font-semibold tabular-nums text-gray-700">
                    {money(change)}
                  </div>
                </div>

                <div className="h-16 border-b border-gray-200 flex items-center px-8">
                  <div className="text-lg font-semibold tabular-nums text-gray-700">
                    {money(Math.max(0, totals.total - paidAmount))}
                  </div>
                </div>

                <div className="h-16 border-b border-gray-200 flex items-center px-8">
                  <div className="text-xl font-semibold text-gray-700">
                    Walk In Customer
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="p-0">
            <Button
              type="button"
              className="h-16 w-full rounded-none bg-[#0a2236] text-white hover:bg-[#0a2236]/90 tracking-[0.25em] text-sm shadow-[0_-6px_24px_rgba(10,34,54,0.25)]"
              onClick={onConfirmPay}
              disabled={saving}
            >
              {saving ? 'SAVING…' : 'CONFIRM'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Small utility buttons (optional) */}
      <div className="sr-only">
        <Button type="button" variant="outline" onClick={keypad.focusScan}>
          <ScanBarcode className="h-4 w-4 mr-2" />
          Focus Scan
        </Button>
      </div>
    </div>
  )
}
